import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { ChartData, ChartDataset, ChartOptions } from 'chart.js';
import { AlertService } from 'src/app/services/auth';
import { MenuService } from 'src/app/services/menu/menu-service';
import { RejectReason } from 'src/app/shared/models/RejectReason';
import { ReturnReason } from 'src/app/shared/models/returnReason';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { ModalExportarComponent } from 'src/app/shared/ui/modal-exportar/modal-exportar.component';
import { AridosHistoryDetailComponent } from './components/aridos-history-detail.component';
import { AridosHistoryFacade } from './aridos-history.facade';
import { AridosHistoryRow, AridosHistorySummary } from './aridos-history.types';
import { UiTableColumn } from 'src/app/shared/ui/table/ui-table.types';

@Component({
  selector: 'app-aridos-history',
  templateUrl: './aridos-history.component.html',
  styleUrls: ['./aridos-history.component.css'],
  standalone: false,
})
export class AridosHistoryComponent implements OnInit, OnDestroy {
  readonly profileName = 'Áridos';
  readonly modalTitle = 'Exportar tabla de datos';
  readonly modalMessage = '<br>El reporte se exportará en archivo excel <br>según los filtros seleccionados.';
  readonly tableColumns: UiTableColumn<AridosHistoryRow>[] = [
    { key: 'orderNumber', header: 'N° Pedido', className: 'ui-col-order' },
    { key: 'workName', header: 'Obra', className: 'ui-col-wrap' },
    { key: 'deliveryAddress', header: 'Dirección', className: 'ui-col-wrap' },
    { key: 'deliveryDate', header: 'Fecha y hora entregada', className: 'ui-col-date', value: (row) => `${row.orderDate}, ${this.toAmOrPm(row.startTime)}` },
    { key: 'signature', header: 'Firma', className: 'ui-col-signature' },
    { key: 'guide', header: 'Guía', className: 'ui-col-guide' },
    { key: 'status', header: 'Estado', className: 'ui-col-status' },
    { key: 'actions', header: 'Acciones', className: 'ui-col-actions' },
  ];
  readonly mobileTableColumns: UiTableColumn<AridosHistoryRow>[] = [
    { key: 'workName', header: 'Obra' },
    { key: 'deliveryAddress', header: 'Dirección' },
    { key: 'productDescription', header: 'Producto' },
    { key: 'deliveryDate', header: 'Fecha y hora', value: (row) => `${row.orderDate}, ${this.toAmOrPm(row.startTime)}` },
    { key: 'quantity', header: 'Cantidad', value: (row) => `${row.actualQuantityShipped} ${row.um}` },
    { key: 'signature', header: 'Firma' },
    { key: 'guide', header: 'Guía' },
    { key: 'status', header: 'Estado' },
    { key: 'actions', header: 'Acciones' },
  ];
  orders: AridosHistoryRow[] = [];
  ordersOriginal: AridosHistoryRow[] = [];
  order: AridosHistoryRow | null = null;
  rejectReasons: RejectReason[] = [];
  returnReasons: ReturnReason[] = [];
  numberOrdersDelivered = 0;
  numberOrdersRejected = 0;
  numberOrdersPending = 0;
  isNotificationVisible = false;
  fechaMinimaVueltas = new Date();
  fechaMaximaVueltas = new Date();
  totalVueltas = 0;
  guias_firmadas = 0;
  guias_no_firmadas = 0;
  guias_rechazadas = 0;
  resumenDespacho: AridosHistorySummary['rows'] = [];
  datosTabla: Array<{ ticket: string; obra: string; direccion: string }> = [];
  chartData: ChartDataset<'bar', number[]>[] = [];
  chartLabels: string[] = [];
  firmadasChartData: ChartData<'pie', number[], string> = { datasets: [] };
  readonly firmadasChartLabels = ['Firmadas', 'No firmadas', 'Rechazadas'];
  readonly chartOptions: ChartOptions = { responsive: true, scales: { y: { beginAtZero: true } } };
  readonly firmadasChartOptions: ChartOptions = { responsive: true, plugins: { legend: { position: 'top', onClick: null }, tooltip: { enabled: true } } };
  readonly filtrosForm = new UntypedFormGroup({ numeroPedido: new UntypedFormControl(''), numeroGuia: new UntypedFormControl(''), fechaDesde: new UntypedFormControl(''), fechaHasta: new UntypedFormControl(''), estado: new UntypedFormControl(''), firma: new UntypedFormControl('') });
  private readonly destroy$ = new Subject<void>();
  private readonly loadOrders$ = new Subject<boolean>();
  private ignoreWarmupOrdersUpdateUntil = 0;
  @ViewChild('modalExporta', { static: true }) modalExporta!: ModalExportarComponent;
  @ViewChild('detallePedido', { static: true }) detallePedido!: AridosHistoryDetailComponent;
  @ViewChild('picker') datePicker!: MatDateRangePicker<Date>;
  activeTab = 2;
  readonly tabs = [{ title: 'Historial' }, { title: 'Resumen vueltas ' }];

  constructor(
    public readonly historyFacade: AridosHistoryFacade,
    private readonly menu: MenuService,
    private readonly router: Router,
    private readonly alertService: AlertService,
  ) { this.menu.btnPrevius = true; }

  ngOnInit(): void {
    this.historyFacade.getReturnReasons().pipe(takeUntil(this.destroy$)).subscribe((data) => (this.returnReasons = data));
    this.historyFacade.getRejectReasons().pipe(takeUntil(this.destroy$)).subscribe((data) => (this.rejectReasons = data));
    this.loadOrders$.pipe(debounceTime(80), switchMap((forceNetwork) => this.historyFacade.loadOrders(forceNetwork)), takeUntil(this.destroy$)).subscribe((data) => { this.applyOrdersFilter(data); this.aplicarFiltros(); });
    this.historyFacade.ordersUpdated$.pipe(takeUntil(this.destroy$)).subscribe(() => { if (Date.now() >= this.ignoreWarmupOrdersUpdateUntil) this.loadOrders$.next(false); });
    this.filtrosForm.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => { if (this.filtrosForm.valid) this.aplicarFiltros(); });
    this.updateUrl();
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }
  private applyOrdersFilter(data: AridosHistoryRow[]): void {
    this.ordersOriginal = data.filter((order) => order.status >= 6 || order.status === 0 || order.rejectionFlag !== '' || order.isNotSigned || order.isSignature || !moment(order.orderDate, 'DD/MM/YYYY').isAfter(moment().subtract(1, 'days'))).map((order) => ({ ...order, isOpen: true }));
    this.orders = this.ordersOriginal.filter((order) => order.flag_pending !== '1');
    this.numberOrdersDelivered = this.orders.filter((order) => this.isDelivered(order)).length;
    this.numberOrdersRejected = this.orders.filter((order) => this.isRejected(order)).length;
    this.numberOrdersPending = this.orders.filter((order) => this.isPending(order)).length;
  }
  isRejected(order: AridosHistoryRow): boolean { return order.rejectionFlag !== ''; }
  isDelivered(order: AridosHistoryRow): boolean { return (order.status >= 6 || order.status === 0 || order.isSignature) && order.rejectionFlag === ''; }
  isPending(order: AridosHistoryRow): boolean { return order.status < 6 && order.status !== 0 && order.rejectionFlag === '' && !order.isSignature; }
  isSigned(order: AridosHistoryRow): boolean { return order.isSignature; }
  rowTitle(order: AridosHistoryRow): string { return `N° Guía ${order.folioNumberGD}`; }
  toAmOrPm(time: string): string { if (!time) return ''; const [rawHour, minute = '00'] = time.split(':'); const hour = Number(rawHour); if (Number.isNaN(hour)) return time; return hour >= 12 ? `${hour > 12 ? hour - 12 : hour}:${minute} PM.` : `${hour}:${minute} AM.`; }
  aplicarFiltros(): void {
    const { numeroPedido, numeroGuia, fechaDesde, fechaHasta, estado, firma } = this.filtrosForm.value;
    const pedido = String(numeroPedido ?? '').trim().toLowerCase(); const guia = String(numeroGuia ?? '').trim();
    this.orders = this.ordersOriginal.filter((order) => {
      const matchSearch = (!pedido && !guia) || ((!guia || String(order.folioNumberGD ?? '').includes(guia)) && (!pedido || String(order.workName ?? '').toLowerCase().includes(pedido) || String(order.orderNumber ?? '').includes(pedido)));
      const matchStatus = !estado || estado === 'Todos' || (estado === 'Anulado' && this.isRejected(order)) || (estado === 'Pendiente' && this.isPending(order)) || (estado === 'Entregado' && this.isDelivered(order));
      const matchSign = !firma || this.isSigned(order) === (firma === 'Si');
      const matchFrom = !fechaDesde || moment(order.orderDate, 'DD/MM/YYYY').isSameOrAfter(moment(fechaDesde));
      const matchTo = !fechaHasta || moment(order.orderDate, 'DD/MM/YYYY').isSameOrBefore(moment(fechaHasta));
      return matchSearch && matchStatus && matchSign && matchFrom && matchTo;
    });
  }
  limpiarFiltros(): void { this.filtrosForm.reset({ numeroPedido: '', numeroGuia: '', fechaDesde: '', fechaHasta: '', estado: '', firma: '' }); this.orders = this.ordersOriginal; }
  @HostListener('document:click', ['$event'])
  closeDatePickerOnOutsideClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!this.datePicker?.opened || target.closest('.date-range-trigger') || target.closest('.cdk-overlay-container')) return;
    this.datePicker.close();
  }

  dateRangeLabel(): string {
    const from = this.filtrosForm.get('fechaDesde')?.value;
    const to = this.filtrosForm.get('fechaHasta')?.value;
    const fromLabel = from ? moment(from).format('DD/MM/YYYY') : '';
    const toLabel = to ? moment(to).format('DD/MM/YYYY') : '';
    return fromLabel && toLabel ? `${fromLabel} | ${toLabel}` : 'Desde | Hasta';
  }
  goDetalle(order: AridosHistoryRow): void { this.order = order; this.detallePedido.openDetalle(); }
  generateQr(order: AridosHistoryRow): void { this.historyFacade.regenerateQr(String(order.tkt), String(order.orderNumber)).subscribe({ next: (data: any) => { if (data?.Estado === 'Insertado') this.router.navigate(['/PedidosPendientes/Detalle'], { queryParams: { tkt: order.tkt, paso: 3 } }); else this.alertService.error('Error al generar QR.'); }, error: () => this.alertService.error('Error al generar QR.') }); }
  generarDashboard(from: string, to: string): void { this.historyFacade.loadSummary(from, to).subscribe({ next: (summary) => this.applySummary(summary), error: (error) => console.error('Error al obtener resumen:', error) }); }
  private applySummary(summary: AridosHistorySummary): void { this.resumenDespacho = summary.rows; this.datosTabla = summary.rows.map((row) => ({ ticket: row.ticket, obra: row.workName, direccion: row.deliveryAddress })); this.guias_firmadas = summary.signed; this.guias_no_firmadas = summary.unsigned; this.guias_rechazadas = summary.rejected; this.totalVueltas = summary.total; const weeks = [...new Set(summary.rows.map((row) => row.week))].sort(); this.chartLabels = weeks.map((week) => `Semana ${week}`); this.chartData = [{ data: weeks.map((week) => summary.rows.filter((row) => row.week === week).length), label: 'Vueltas', backgroundColor: 'rgba(40, 167, 69, 0.3)', borderColor: '#28a745', borderWidth: 2 }]; this.firmadasChartData = { labels: this.firmadasChartLabels, datasets: [{ data: [summary.signed, summary.unsigned, summary.rejected], backgroundColor: ['#d1e7dd', '#fff3cd', '#f8d7da'], borderColor: ['#0f5132', '#664d03', '#842029'], borderWidth: 2 }] }; }
  filtrarPorFechas(fechas: [string, string]): void { this.generarDashboard(fechas[0], fechas[1]); }
  getExportRows(): unknown[] { return this.historyFacade.getExportData(this.orders); }
  showNotification(): void { this.isNotificationVisible = true; }
  hideNotification(): void { this.isNotificationVisible = false; }
  updateUrl(): void { this.ignoreWarmupOrdersUpdateUntil = Date.now() + 4000; this.historyFacade.refresh(); this.loadOrders$.next(true); }
}
